import { Capacitor } from '@capacitor/core';
import { CapacitorCalendar } from '@ebarooni/capacitor-calendar';
import { calendar } from '../api';

// The iPhone's own (EventKit) calendar, read on the phone and pushed to the server so the coach's managers and the
// headcoach can see it. Only meaningful in the iOS app; everywhere else isAvailable() is false and nothing is imported.
const FLAG = 'cpDeviceCalendarOn';
const DAYS_BACK = 7;
const DAYS_AHEAD = 60;
const MIN_GAP_MS = 5 * 60 * 1000; // automatic syncs are at most this often
let lastSync = 0;
let syncing = null;

export const isAvailable = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

export const isEnabled = () => {
  try { return localStorage.getItem(FLAG) === '1'; } catch (e) { return false; }
};
const setEnabled = (on) => {
  try { if (on) localStorage.setItem(FLAG, '1'); else localStorage.removeItem(FLAG); } catch (e) { /* ignore */ }
};

// A step that never answers would leave the button on "Linking..." forever - fail with a message saying which step stalled.
const step = (label, promise, ms) => Promise.race([
  promise,
  new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} did not respond. Close and reopen the app, then try again.`)), ms))
]);

// Reads the window from the phone and replaces the server's copy. Returns { eventCount, syncedAt }.
async function pushNow() {
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const from = new Date(start); from.setDate(from.getDate() - DAYS_BACK);
  const to = new Date(start); to.setDate(to.getDate() + DAYS_AHEAD);

  const { result } = await step('Reading the iPhone calendar', CapacitorCalendar.listEventsInRange({ from: from.getTime(), to: to.getTime() }), 20000);
  const events = (result || []).map(e => ({
    id: `${e.id}:${e.startDate}`, // a repeating event keeps one id for every occurrence
    title: e.title,
    notes: e.description,
    location: e.location,
    calendarTitle: '',
    allDay: !!e.isAllDay,
    start: new Date(e.startDate).toISOString(),
    end: new Date(e.endDate).toISOString()
  }));

  const res = await step('The CoachPad server', calendar.syncDeviceEvents({ events, from: from.toISOString(), to: to.toISOString() }), 30000);
  lastSync = Date.now();
  return res.data;
}

// Asks iOS for calendar access, then does the first sync.
export async function connect() {
  const { result } = await step('The calendar permission request', CapacitorCalendar.requestFullCalendarAccess(), 60000);
  if (result !== 'granted') {
    throw new Error('Calendar access was not allowed. Turn it on in iPhone Settings → CoachPad → Calendars, then try again.');
  }
  const data = await pushNow();
  setEnabled(true);
  return data;
}

export async function disconnect() {
  setEnabled(false);
  await calendar.disconnectDevice();
}

// Quiet background refresh (app open / back to foreground). Never throws.
export async function syncIfDue(force = false) {
  if (!isAvailable() || !isEnabled()) return;
  if (!force && Date.now() - lastSync < MIN_GAP_MS) return;
  if (syncing) return syncing;
  syncing = pushNow().catch(() => { /* try again next time */ }).finally(() => { syncing = null; });
  return syncing;
}
